import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardBody, Col, Row, Media, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import DataTable from '../../../Common/DataTable';
import { PlusCircle, Edit, ArrowRight, DollarSign } from 'react-feather';
import EnforcementDataModal from './EnforcementDataModal';
import ExpenseDataModal from '../../FinancialOperations/Expenses/ExpenseDataModal';
import PaymentCollectionDataModal from '../../FinancialOperations/PaymentCollections/PaymentCollectionDataModal';
import PaymentCollectionListModal from '../../FinancialOperations/PaymentCollections/PaymentCollectionListModal';
import { toast } from 'react-toastify';
import { fetchTableData, handleStateTransition, fetchFormData, createFinalCompensation } from '../../../../Api/ApiMethods';
import FilesCollapsible from '../../../Common/FilesCollapsible';
import { shouldToastError } from '../../../../Api/utils';

const EnforcementInformations = ({ arbitrationId, arbitrationData, refreshArbitrationData }) => {
    const [, setSelectedRows] = useState([]);
    const [clearSelectedRows] = useState(false);
    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [selectedEnforcement, setSelectedEnforcement] = useState(null);

    const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
    const [currentEnforcement, setCurrentEnforcement] = useState(null);
    const [selectedExpense, setSelectedExpense] = useState(null);
    const [expenseModalMode, setExpenseModalMode] = useState('add');
    const [expenseSchemaType, setExpenseSchemaType] = useState('TAKIP_MASRAFI');
    const [isExpenseReadOnly, setIsExpenseReadOnly] = useState(false);

    const [isExpenseListModalOpen, setIsExpenseListModalOpen] = useState(false);
    const [currentExpenseList, setCurrentExpenseList] = useState([]);
    const [currentExpenseListType, setCurrentExpenseListType] = useState('');
    const [currentEnforcementForList, setCurrentEnforcementForList] = useState(null);

    const [isPaymentCollectionListModalOpen, setIsPaymentCollectionListModalOpen] = useState(false);
    const [paymentCollections, setPaymentCollections] = useState([]);
    const [isPaymentCollectionsLoading, setIsPaymentCollectionsLoading] = useState(false);
    const [paymentCollectionModalOpen, setPaymentCollectionModalOpen] = useState(false);
    const [paymentCollectionModalMode, setPaymentCollectionModalMode] = useState('add');
    const [selectedPaymentCollection, setSelectedPaymentCollection] = useState(null);
    const [currentEnforcementForPayments, setCurrentEnforcementForPayments] = useState(null);
    const [finalCompensationSubmittingId, setFinalCompensationSubmittingId] = useState(null);

    const enforcementApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/Enforcement';
    const paymentCollectionApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/paymentcollection';

    const primaryAccidentFileId = React.useMemo(() => {
        const accidentFile = arbitrationData?.accidentFile;

        const resolveId = (entry) => {
            if (!entry) return null;
            if (typeof entry === 'object') {
                return entry.id || entry.accidentFileId || null;
            }
            return entry;
        };

        if (!accidentFile) {
            return null;
        }

        if (Array.isArray(accidentFile)) {
            for (const entry of accidentFile) {
                const resolved = resolveId(entry);
                if (resolved) {
                    return resolved;
                }
            }
            return null;
        }

        return resolveId(accidentFile);
    }, [arbitrationData]);

    const primaryAccidentFileLabel = React.useMemo(() => {
        const accidentFile = arbitrationData?.accidentFile;

        const resolveLabel = (entry) => {
            if (!entry || typeof entry !== 'object') return null;
            return entry.fileNumber || entry.fileNo || entry.accidentFileNumber || entry.fileName || null;
        };

        if (!accidentFile) {
            return null;
        }

        if (Array.isArray(accidentFile)) {
            for (const entry of accidentFile) {
                const resolved = resolveLabel(entry);
                if (resolved) {
                    return resolved;
                }
            }
            return null;
        }

        return resolveLabel(accidentFile);
    }, [arbitrationData]);

    const currentEnforcementId = React.useMemo(() => {
        if (!currentEnforcementForPayments) {
            return null;
        }

        const rawId = currentEnforcementForPayments.id
            || currentEnforcementForPayments._id
            || currentEnforcementForPayments.enforcementId
            || null;

        return rawId ? String(rawId) : null;
    }, [currentEnforcementForPayments]);

    const canSendToCover = React.useMemo(() => {
        const currentState = arbitrationData?.arbitrationState?.title;
        return ['Takip Masrafı Ödemesi Bekleniyor', 'Tahsil Harcı Ödemesi Bekleniyor', 'İcra Başvuru Bekleniyor'].includes(currentState);
    }, [arbitrationData]);

    const handleRowSelected = useCallback(state => {
        setSelectedRows(state.selectedRows);
    }, []);

    useEffect(() => {
        if (arbitrationId) {
            const modifiedApiEndpoint = `${enforcementApiUrl}?relatedArbitrationId=${arbitrationId}`;
            
            fetchTableData({
                apiEndpoint: modifiedApiEndpoint,
                page,
                pageSize,
                search: '',
                setDataCallback: setData,
                setTotalRowsCallback: setTotalRows,
            });
        }
    }, [page, pageSize, arbitrationId, enforcementApiUrl]);

    const toggleModal = () => {
        setModalOpen(!modalOpen);
        if (modalOpen) {
            setTimeout(() => setSelectedEnforcement(null), 300);
        }
    };

    const openAddModal = () => {
        setModalMode('add');
        setSelectedEnforcement(null);
        setModalOpen(true);
    };

    const openModifyModal = (enforcement) => {
        setModalMode('modify');
        setSelectedEnforcement(enforcement);
        setModalOpen(true);
    };

    const isSentenced = arbitrationData?.accidentFile?.isSentenced ?? true;

    const refreshEnforcementData = () => {
        if (arbitrationId) {
            const modifiedApiEndpoint = `${enforcementApiUrl}?relatedArbitrationId=${arbitrationId}`;

            fetchTableData({
                apiEndpoint: modifiedApiEndpoint,
                page,
                pageSize,
                search: '',
                setDataCallback: setData,
                setTotalRowsCallback: setTotalRows,
            });
        }
        refreshArbitrationData();
    };

    const handleViewTrackingExpensesModal = (enforcement) => {
        const trackingExpenses = enforcement.trackingDetailExpenses || [];
        setCurrentExpenseList(trackingExpenses);
        setCurrentExpenseListType('Takip Masrafı');
        setCurrentEnforcementForList(enforcement);
        setIsExpenseListModalOpen(true);
    };

    const handleViewCollectionFeesModal = (enforcement) => {
        const collectionFees = enforcement.collectionFeeExpenses || [];
        setCurrentExpenseList(collectionFees);
        setCurrentExpenseListType('Tahsil Harcı');
        setCurrentEnforcementForList(enforcement);
        setIsExpenseListModalOpen(true);
    };

    const closeExpenseModal = () => {
        setIsExpenseModalOpen(false);
        setCurrentEnforcement(null);
        setSelectedExpense(null);
        setExpenseSchemaType('TAKIP_MASRAFI');
        setIsExpenseReadOnly(false);
    };

    const closeExpenseListModal = () => {
        setIsExpenseListModalOpen(false);
        setCurrentExpenseList([]);
        setCurrentExpenseListType('');
        setCurrentEnforcementForList(null);
    };

    const handleAddExpenseFromList = () => {
        setCurrentEnforcement(currentEnforcementForList);
        setSelectedExpense(null);
        setExpenseModalMode('add');
        setExpenseSchemaType(currentExpenseListType === 'Takip Masrafı' ? 'TAKIP_MASRAFI' : currentExpenseListType === 'Tahsil Harcı' ? 'TAHSIL_HARCI' : currentExpenseListType);
        setIsExpenseReadOnly(false);
        closeExpenseListModal();
        setIsExpenseModalOpen(true);
    };

    const handleEditExpenseFromList = (expense) => {
        // Durum kısıtlaması kaldırıldı — İcra Başvuru Bekleniyor'da da düzenlenebilir
        // Eski mantık (ileride geri açılabilir):
        // const currentState = arbitrationData?.arbitrationState?.title;
        // const canEdit = (currentExpenseListType === 'Takip Masrafı' && currentState === 'Takip Masrafı Ödemesi Bekleniyor') ||
        //                (currentExpenseListType === 'Tahsil Harcı' && currentState === 'Tahsil Harcı Ödemesi Bekleniyor');

        setCurrentEnforcement(currentEnforcementForList);
        setSelectedExpense(expense);
        setExpenseModalMode('modify');
        setExpenseSchemaType(currentExpenseListType === 'Takip Masrafı' ? 'TAKIP_MASRAFI' : currentExpenseListType === 'Tahsil Harcı' ? 'TAHSIL_HARCI' : currentExpenseListType);
        setIsExpenseReadOnly(false);
        closeExpenseListModal();
        setIsExpenseModalOpen(true);
    };

    const refreshExpenseData = () => {
        refreshEnforcementData();
    };

    const fetchPaymentCollectionsForEnforcement = useCallback(async (enforcementId) => {
        if (!enforcementId) {
            setPaymentCollections([]);
            return;
        }

        setIsPaymentCollectionsLoading(true);
        const normalizedId = String(enforcementId);

        try {
            const response = await fetchFormData({
                apiEndpoint: `${paymentCollectionApiUrl}?enforcement=${encodeURIComponent(normalizedId)}`,
                page: 1,
                pageSize: 100,
            });

            let collections = [];

            if (response?.isSuccess && Array.isArray(response?.data?.items)) {
                collections = response.data.items;
            } else if (Array.isArray(response?.items)) {
                collections = response.items;
            } else if (Array.isArray(response)) {
                collections = response;
            } else if (response?.data && Array.isArray(response.data)) {
                collections = response.data;
            }

            setPaymentCollections(collections);
        } catch (error) {
            if (shouldToastError(error)) {
                toast.error('Tahsilatlar yüklenirken bir hata oluştu.');
            }
            setPaymentCollections([]);
        } finally {
            setIsPaymentCollectionsLoading(false);
        }
    }, [paymentCollectionApiUrl]);

    const refreshPaymentCollections = useCallback(() => {
        if (currentEnforcementId) {
            fetchPaymentCollectionsForEnforcement(currentEnforcementId);
        }
    }, [fetchPaymentCollectionsForEnforcement, currentEnforcementId]);

    const handleOpenPaymentCollectionListModal = (enforcement) => {
        const enforcementId = enforcement?.id || enforcement?._id;

        if (!enforcementId) {
            toast.warn('İcra kaydı kimliği bulunamadı. Tahsilat işlemleri için geçerli bir icra seçilmelidir.');
            return;
        }

        setCurrentEnforcementForPayments(enforcement);
        fetchPaymentCollectionsForEnforcement(enforcementId);
        setIsPaymentCollectionListModalOpen(true);
    };

    const closePaymentCollectionListModal = () => {
        setIsPaymentCollectionListModalOpen(false);
        if (paymentCollectionModalOpen) {
            setPaymentCollectionModalOpen(false);
            setTimeout(() => setSelectedPaymentCollection(null), 300);
        } else {
            setSelectedPaymentCollection(null);
        }
        setPaymentCollections([]);
        setCurrentEnforcementForPayments(null);
    };

    const handleAddPaymentCollection = () => {
        setSelectedPaymentCollection(null);
        setPaymentCollectionModalMode('add');
        setPaymentCollectionModalOpen(true);
    };

    const handleEditPaymentCollection = (collection) => {
        setSelectedPaymentCollection(collection);
        setPaymentCollectionModalMode('modify');
        setPaymentCollectionModalOpen(true);
    };

    const handleSendPaymentCollectionToCover = async (collection) => {
        if (!collection) {
            toast.warn('Geçerli bir tahsilat seçilmedi.');
            return;
        }

        if (!canSendToCover) {
            toast.warn('Tahkim mevcut durumundan dolayı kapağa gönderme işlemi yapılamaz.');
            return;
        }

        const collectionId = collection.id || collection._id;

        if (!collectionId) {
            toast.warn('Tahsilat kaydı kimliği bulunamadı.');
            return;
        }

        if (!currentEnforcementId) {
            toast.warn('Tahsilatın bağlı olduğu icra kaydı bulunamadı.');
            return;
        }

        if (!arbitrationId) {
            toast.warn('Tahkim kimliği bulunamadı.');
            return;
        }

        if (!primaryAccidentFileId) {
            toast.warn('Tahsilatın bağlı olduğu kaza dosyası bulunamadı.');
            return;
        }

        const payload = {
            paymentCollections: [String(collectionId)],
            accidentFile: primaryAccidentFileId ? [String(primaryAccidentFileId)] : [],
            arbitration: [String(arbitrationId)],
            enforcement: [String(currentEnforcementId)]
        };

        setFinalCompensationSubmittingId(collectionId);

        try {
            await createFinalCompensation(payload);

            if (currentEnforcementId) {
                await fetchPaymentCollectionsForEnforcement(currentEnforcementId);
            }

            refreshArbitrationData();
        } catch {
            // Hata sessizce yutulur
        } finally {
            setFinalCompensationSubmittingId(null);
        }
    };

    const togglePaymentCollectionModal = () => {
        if (paymentCollectionModalOpen) {
            setPaymentCollectionModalOpen(false);
            setTimeout(() => setSelectedPaymentCollection(null), 300);
        } else {
            setPaymentCollectionModalOpen(true);
        }
    };


    useEffect(() => {
        if (isPaymentCollectionListModalOpen && currentEnforcementId) {
            fetchPaymentCollectionsForEnforcement(currentEnforcementId);
        }
    }, [isPaymentCollectionListModalOpen, currentEnforcementId, fetchPaymentCollectionsForEnforcement]);

    // const handleTrackingExpenseTransition = async () => {
    //     try {
    //         await handleStateTransition({
    //             arbitrationId,
    //             goToPayTrackingDetailFee: true
    //         });
    //         refreshArbitrationData();
    //     } catch {
    //         // Hata sessizce yutulur
    //     }
    // };

    // const handleCollectionFeeTransition = async () => {
    //     try {
    //         await handleStateTransition({
    //             arbitrationId,
    //             goToPayCollectionFee: true
    //         });
    //         refreshArbitrationData();
    //     } catch {
    //         // Hata sessizce yutulur
    //     }
    // };

    const handleCloseFileTransition = async () => {
        try {
            await handleStateTransition({
                arbitrationId,
                goToCloseFile: true
            });
            refreshArbitrationData();
        } catch {
            // Hata sessizce yutulur
        }
    };

    const getButtonConfig = () => {
        const currentState = arbitrationData?.arbitrationState?.title;
        const buttons = [];

        // Takip Masrafı Ödemesi ve Tahsil Harcı Ödemesi durumları devre dışı bırakıldı.
        // İcra Başvuru Bekleniyor'dan direkt ödeme eklenebilir, durum geçişi gerekmez.
        // İleride tekrar aktif edilebilir.

        // if (currentState === 'Takip Masrafı Ödemesi Bekleniyor') {
        //     buttons.push({
        //         key: 'collectionFee',
        //         text: 'Tahsil Harcı Ödemesine Gönder',
        //         action: handleCollectionFeeTransition,
        //         className: 'btn-outline-warning',
        //         icon: 'ArrowRight'
        //     });
        //     buttons.push({
        //         key: 'closeFile',
        //         text: 'Dosya Kapamaya Gönder',
        //         action: handleCloseFileTransition,
        //         className: 'btn-outline-danger',
        //         icon: 'ArrowRight'
        //     });
        // } else if (currentState === 'Tahsil Harcı Ödemesi Bekleniyor') {
        //     buttons.push({
        //         key: 'trackingExpense',
        //         text: 'Takip Masrafı Ödemesine Gönder',
        //         action: handleTrackingExpenseTransition,
        //         className: 'btn-outline-success',
        //         icon: 'ArrowRight'
        //     });
        //     buttons.push({
        //         key: 'closeFile',
        //         text: 'Dosya Kapamaya Gönder',
        //         action: handleCloseFileTransition,
        //         className: 'btn-outline-danger',
        //         icon: 'ArrowRight'
        //     });
        // } else

        if (currentState === 'İcra Başvuru Bekleniyor') {
            buttons.push({
                key: 'closeFile',
                text: 'Dosya Kapamaya Gönder',
                action: handleCloseFileTransition,
                className: 'btn-outline-danger',
                icon: 'ArrowRight'
            });
        }

        return buttons;
    };

    const formatTrackingDetailTypes = (types) => {
        if (!types || !Array.isArray(types) || types.length === 0) return '-';
        
        if (types.length <= 2) {
            return types.join(', ');
        } else {
            return types.slice(0, 2).join(', ') + '...';
        }
    };

    const columns = [
        {
            name: 'Takip Tarihi',
            selector: row => row.followUpDate ? new Date(row.followUpDate).toLocaleDateString('tr-TR') : '-',
            sortable: true,
            minWidth: '120px',
        },
        {
            name: 'FOY No',
            selector: row => row.foyNumber || '-',
            sortable: true,
            minWidth: '120px',
        },
        {
            name: 'Dosya Durumu',
            selector: row => row.enforcementStatus || '-',
            sortable: true,
            minWidth: '130px',
        },
        {
            name: 'Adliye İsmi',
            selector: row => row.courtOffice || '-',
            sortable: true,
            minWidth: '150px',
        },
        {
            name: 'İcra Esas Numarası',
            selector: row => row.enforcementCaseNumber || '-',
            sortable: true,
            minWidth: '150px',
        },
        {
            name: 'Takip Detay Türü',
            selector: row => formatTrackingDetailTypes(row.trackingDetailType),
            sortable: false,
            minWidth: '150px',
            cell: row => (
                <div title={Array.isArray(row.trackingDetailType) ? row.trackingDetailType.join(', ') : '-'}>
                    {formatTrackingDetailTypes(row.trackingDetailType)}
                </div>
            ),
        },
        {
            name: 'Takip Masrafı',
            cell: (row) => {
                const trackingAmount = row.trackingDetailAmount || 0;
                const trackingExpenses = row.trackingDetailExpenses || [];

                return (
                    <div
                        className="btn btn-outline-primary d-flex align-items-center justify-content-center"
                        onClick={() => handleViewTrackingExpensesModal(row)}
                        title={`${trackingExpenses.length} adet masraf - Detayları görüntülemek için tıklayın`}
                        style={{ cursor: 'pointer', minWidth: '100px' }}
                    >
                        <small className="text-primary fw-bold">
                            {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(trackingAmount)}
                        </small>
                    </div>
                );
            },
            ignoreRowClick: true,
            button: true,
            grow: 1.2,
            minWidth: '130px'
        },
        {
            name: 'Tahsil Harcı',
            cell: (row) => {
                const collectionAmount = row.collectionFeeAmount || 0;
                const collectionFees = row.collectionFeeExpenses || [];

                return (
                    <div
                        className="btn btn-outline-primary d-flex align-items-center justify-content-center"
                        onClick={() => handleViewCollectionFeesModal(row)}
                        title={`${collectionFees.length} adet harç - Detayları görüntülemek için tıklayın`}
                        style={{ cursor: 'pointer', minWidth: '100px' }}
                    >
                        <small className="text-primary fw-bold">
                            {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(collectionAmount)}
                        </small>
                    </div>
                );
            },
            ignoreRowClick: true,
            button: true,
            grow: 1.2,
            minWidth: '130px'
        },
        {
            name: 'Düzenle',
            cell: (row) => (
                <div
                    className="btn btn-outline-warning d-flex align-items-center justify-content-center"
                    onClick={() => openModifyModal(row)}
                    title="Düzenle"
                >
                    <Edit size={18} />
                </div>
            ),
            ignoreRowClick: true,
            right: true,
            button: true,
            grow: 1.2,
            minWidth: '100px'
        },
        {
            name: 'Tahsilatlar',
            cell: (row) => (
                <div
                    className="btn btn-outline-success d-flex align-items-center justify-content-center"
                    onClick={() => handleOpenPaymentCollectionListModal(row)}
                    title="Tahsilatları Görüntüle / Ekle"
                >
                    <DollarSign size={18} />
                </div>
            ),
            ignoreRowClick: true,
            right: true,
            button: true,
            grow: 1.2,
            minWidth: '110px'
        },
    ];

    return (
        <>
            <Row className="mt-4">
                <Col sm='12'>
                    <Card>
                        <CardBody>
                            <div className="d-flex justify-content-end align-items-center w-100 file-content flex-wrap">
                                <Media body className='text-end mt-3'>
                                    <div className="btn btn-outline-primary ms-2" onClick={openAddModal} style={{ height: '35px', display: 'inline-flex', alignItems: 'center' }}>
                                        <PlusCircle size={18} className="me-1" />
                                        {' İcra Ekle'}
                                    </div>
                                    {getButtonConfig().map((btnConfig) => (
                                        <div
                                            key={btnConfig.key}
                                            className={`btn ${btnConfig.className} ms-2`}
                                            onClick={btnConfig.action}
                                            style={{ height: '35px', display: 'inline-flex', alignItems: 'center' }}
                                        >
                                            {btnConfig.icon === 'ArrowRight' && <ArrowRight size={18} className="me-1" />}
                                            {btnConfig.text}
                                        </div>
                                    ))}
                                </Media>
                            </div>

                            <div style={{ height: "40px" }}></div>

                            <div className='table-responsive'>
                                <DataTable
                                    columns={columns}
                                    data={data}
                                    onSelectedRowsChange={handleRowSelected}
                                    pagination
                                    paginationServer
                                    clearSelectedRows={clearSelectedRows}
                                    paginationTotalRows={totalRows}
                                    onChangePage={page => setPage(page)}
                                    onChangeRowsPerPage={pageSize => { setPage(1); setPageSize(pageSize); }}
                                    striped
                                    noDataComponent={<div className="p-4 text-center">İcra bilgisi bulunamadı.</div>}
                                    progressPending={!data}
                                    progressComponent={<p>Yükleniyor...</p>}
                                />
                            </div>

                            {Array.isArray(data) && data.length > 0 && (
                                <div className="mt-4">
                                    <h6 className="mb-3">Dosyalar</h6>
                                    {data.map((row, idx) => (
                                        <FilesCollapsible
                                            key={row.id}
                                            title={row.enforcementCaseNumber ? `İcra Esas No: ${row.enforcementCaseNumber}` : `İcra #${idx + 1} Dosyaları`}
                                            referenceId={row.id}
                                        />
                                    ))}
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <EnforcementDataModal
                apiEndpoint={enforcementApiUrl}
                isOpen={modalOpen}
                toggle={toggleModal}
                mode={modalMode}
                selectedEnforcement={selectedEnforcement}
                refreshData={refreshEnforcementData}
                arbitrationId={arbitrationId}
                isSentenced={isSentenced}
                arbitrationData={arbitrationData}
            />

            <PaymentCollectionDataModal
                apiEndpoint={paymentCollectionApiUrl}
                isOpen={paymentCollectionModalOpen}
                toggle={togglePaymentCollectionModal}
                mode={paymentCollectionModalMode}
                selectedPaymentCollection={selectedPaymentCollection}
                refreshData={refreshPaymentCollections}
                accidentFile={primaryAccidentFileId ? [String(primaryAccidentFileId)] : []}
                enforcement={currentEnforcementId ? [currentEnforcementId] : []}
            />

            <PaymentCollectionListModal
                isOpen={isPaymentCollectionListModalOpen}
                toggle={closePaymentCollectionListModal}
                title={`Tahsilatlar${primaryAccidentFileLabel ? ` - Dosya ${primaryAccidentFileLabel}` : ''}`}
                headerExtra={currentEnforcementForPayments && (
                    <div className="mb-3">
                        <strong>İcra Dosyası:</strong> {currentEnforcementForPayments.enforcementCaseNumber || '-'}
                    </div>
                )}
                paymentCollections={paymentCollections}
                isLoading={isPaymentCollectionsLoading}
                submittingId={finalCompensationSubmittingId}
                onEdit={handleEditPaymentCollection}
                onSendToCover={handleSendPaymentCollectionToCover}
                onAdd={handleAddPaymentCollection}
                isAddDisabled={!currentEnforcementId}
                isSendToCoverDisabled={(collection, isSubmitting) => !canSendToCover || isPaymentCollectionsLoading || !currentEnforcementId || !primaryAccidentFileId || isSubmitting}
            />

            <ExpenseDataModal
                apiEndpoint={process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/expense'}
                isOpen={isExpenseModalOpen}
                toggle={closeExpenseModal}
                mode={expenseModalMode}
                selectedExpense={selectedExpense}
                refreshData={refreshExpenseData}
                customerId={null}
                autoAssignCustomer={false}
                accidentFile={[]}
                callformId={null}
                relatedArbitrationSchema={currentEnforcement ? [String(currentEnforcement.id)] : []}
                arbitrationSchemaType={expenseSchemaType}
                readOnlyView={isExpenseReadOnly}
            />

            <Modal isOpen={isExpenseListModalOpen} toggle={closeExpenseListModal} size="xl" backdrop="static">
                <ModalHeader toggle={closeExpenseListModal}>
                    {currentExpenseListType} Detayları
                </ModalHeader>
                <ModalBody>
                    {currentExpenseList.length === 0 ? (
                        <div className="text-center p-4">
                            <p className="text-muted">Henüz {currentExpenseListType.toLowerCase()} kaydı bulunmuyor.</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-striped">
                                <thead>
                                    <tr>
                                        <th style={{ whiteSpace: 'nowrap' }}>Masraf Numarası</th>
                                        <th style={{ whiteSpace: 'nowrap' }}>Masraf Türü</th>
                                        <th style={{ whiteSpace: 'nowrap' }}>Tutar</th>
                                        <th style={{ whiteSpace: 'nowrap' }}>Tarih ve Saat</th>
                                        <th style={{ whiteSpace: 'nowrap' }}>Fatura No</th>
                                        <th style={{ whiteSpace: 'nowrap' }}>Açıklama</th>
                                        <th style={{ whiteSpace: 'nowrap' }}>İşlemler</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentExpenseList.map((expense, index) => (
                                        <tr key={expense.id || index}>
                                            <td style={{ whiteSpace: 'nowrap' }}>{expense.expenseNumber || '-'}</td>
                                            <td style={{ whiteSpace: 'nowrap' }}>{expense.expenseType || '-'}</td>
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                {expense.amount ? 
                                                    new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(expense.amount) 
                                                    : '-'
                                                }
                                            </td>
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                {expense.date ? 
                                                    new Date(expense.date).toLocaleDateString('tr-TR') + ' ' + new Date(expense.date).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
                                                    : '-'
                                                }
                                            </td>
                                            <td style={{ whiteSpace: 'nowrap' }}>{expense.invoiceNumber || '-'}</td>
                                            <td>
                                                <div 
                                                    title={expense.description || '-'}
                                                    style={{
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        maxWidth: '150px'
                                                    }}
                                                >
                                                    {expense.description || '-'}
                                                </div>
                                            </td>
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                <button
                                                    className="btn btn-outline-warning btn-sm"
                                                    onClick={() => handleEditExpenseFromList(expense)}
                                                    title={
                                                        (currentExpenseListType === 'Takip Masrafı' && arbitrationData?.arbitrationState?.title !== 'Takip Masrafı Ödemesi Bekleniyor') ||
                                                        (currentExpenseListType === 'Tahsil Harcı' && arbitrationData?.arbitrationState?.title !== 'Tahsil Harcı Ödemesi Bekleniyor')
                                                        ? "Görüntüle (Sadece Okuma)" : "Düzenle"
                                                    }
                                                >
                                                    <Edit size={14} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <button
                        className="btn btn-success d-flex align-items-center"
                        onClick={handleAddExpenseFromList}
                        /* Durum kısıtlaması kaldırıldı — İcra Başvuru Bekleniyor'da da ekleme serbest
                        disabled={
                            (currentExpenseListType === 'Takip Masrafı' && arbitrationData?.arbitrationState?.title !== 'Takip Masrafı Ödemesi Bekleniyor') ||
                            (currentExpenseListType === 'Tahsil Harcı' && arbitrationData?.arbitrationState?.title !== 'Tahsil Harcı Ödemesi Bekleniyor')
                        }
                        */
                    >
                        <PlusCircle size={16} className="me-2" />
                        {currentExpenseListType} Ekle
                    </button>
                    <button
                        className="btn btn-secondary"
                        onClick={closeExpenseListModal}
                    >
                        Kapat
                    </button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default EnforcementInformations;

